import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Plus, Trash2, Eye, Send, Loader2, RefreshCw, X, Building2, Crown, FlaskConical, FileText, AlertTriangle, CheckCheck, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { aiApi } from "./aiApi";
import AIWabotSettings from "./AIWabotSettings";
import { setMickeyMood, celebrateMickey } from "./mickeyMood";

const KINDS = [
  { k: "daily", label: "Harian", time: "Setiap hari 3:00 ptg" },
  { k: "weekly", label: "Mingguan", time: "Khamis 12:30 tghari" },
  { k: "monthly", label: "Bulanan", time: "1hb, 9:00 pagi" },
  { k: "injury", label: "Kecederaan", time: "Segera bila direkod" },
];
const LOG_LABEL = { daily: "Harian", weekly: "Mingguan", monthly: "Bulanan", monthly_pdf: "PDF Bulanan", connection: "Uji sambungan" };
const logLabel = (t) => {
  if (t === "ai_inbound") return "Mesej masuk";
  if (t === "ai_inbound_reply") return "Balasan Mimi";
  if (t === "ai_inbound_media") return "Lampiran masuk";
  const test = t.endsWith("_test");
  const base = t.replace(/^ai_report_|^ai_injury_alert/, (m) => (m === "ai_injury_alert" ? "injury" : "")).replace(/_test$/, "");
  return (base === "injury" ? "Kecederaan" : LOG_LABEL[base] || base) + (test ? " (ujian)" : "");
};
const fileHref = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const EMPTY = { name: "", phone: "", type: "pengurus", branch: "" };
const ALL = "__all__";

export default function AIWhatsAppSettings({ settings, branches = [], onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null); // {id, kind, message, sender_branch}
  const [logs, setLogs] = useState([]);
  const wa = { daily: true, weekly: true, monthly: true, injury: true, ...(settings.wa_reports || {}) };
  const [fail, setFail] = useState(null);
  const loadFail = () => aiApi.summary().then((r) => setFail(r.wa_failed ? { n: r.wa_failed, last: r.wa_failed_last } : null)).catch(() => {});
  const ack = async () => {
    await aiApi.waAck().catch(() => {});
    setFail(null);
    window.dispatchEvent(new Event("ai:refresh-summary"));
    celebrateMickey(2500);
    toast.success("Ditandakan sudah disemak");
  };
  const recs = settings.wa_recipients || [];

  const loadLogs = () => aiApi.waLogs().then((r) => setLogs(r.logs || [])).catch(() => {});
  useEffect(() => { loadLogs(); loadFail(); }, []);

  const saveRecs = async (list, msg) => {
    try { await onSave({ wa_recipients: list }, msg); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan penerima"); return false; }
  };
  const add = async () => {
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Sila isi nama & nombor telefon");
    if (form.type === "pengurus" && !form.branch) return toast.error("Sila pilih cawangan");
    if (await saveRecs([...recs, { ...form, active: true, reports: { daily: true, weekly: true, monthly: true, injury: true, urgent: true, executive: true, briefing: true } }], "Penerima ditambah")) {
      setForm(EMPTY); setAdding(false);
    }
  };
  const patch = (id, p) => saveRecs(recs.map((r) => (r.id === id ? { ...r, ...p } : r)));
  const remove = (r) => { if (window.confirm(`Padam penerima ${r.name}?`)) saveRecs(recs.filter((x) => x.id !== r.id), "Penerima dipadam"); };

  const test = async (r, kind, send) => {
    setBusy(`${r.id}:${kind}:${send ? "s" : "p"}`);
    setMickeyMood("thinking");
    try {
      const res = await aiApi.waTest(r.id, kind, send);
      setPreview({ id: r.id, kind, message: res.message, sender: res.sender, status: res.status, file_url: res.file_url });
      if (send) {
        if (res.status === "sent") { toast.success(`Laporan ujian dihantar ke ${r.phone}`); celebrateMickey(); }
        else if (res.status === "dry_run") { toast.info("Mod ujian aktif — mesej dilog sahaja, tidak dihantar"); celebrateMickey(); }
        else { toast.error(`Gagal: ${res.response || "tidak dihantar"}`); setMickeyMood("sad", 5000); loadFail(); window.dispatchEvent(new Event("ai:refresh-summary")); }
      } else {
        setMickeyMood("idle");
        loadLogs();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); setMickeyMood("idle"); } finally { setBusy(null); }
  };

  return (
    <div className="rounded-2xl border border-emerald-100 bg-white p-3" data-testid="ai-wa-settings">
      <div className="flex items-center gap-2 mb-1">
        <div className="h-7 w-7 rounded-lg bg-emerald-500 text-white grid place-items-center"><MessageCircle className="h-4 w-4" /></div>
        <div className="font-extrabold text-sm text-slate-800 flex-1">Laporan WhatsApp</div>
      </div>
      <div className="text-[11px] text-slate-500 mb-2 leading-snug">
        Laporan penuh dihantar ke WhatsApp penerima di bawah melalui <b>satu nombor Wabot</b>. Pengurus menerima laporan cawangannya sahaja;
        Superadmin menerima laporan semua cawangan.
      </div>

      {fail && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-2 mb-2 flex items-start gap-2" data-testid="ai-wa-fail-banner">
          <AlertTriangle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0 text-[12px] text-rose-700">
            <b>{fail.n} penghantaran WhatsApp gagal</b> (48 jam lepas).
            {fail.last && <div className="text-[11px] text-rose-600 truncate">Terkini: {fail.last.recipient_name} • {logLabel(fail.last.type)} — {fail.last.response}</div>}
            <div className="text-[11px] text-rose-500">Semak Instance ID / Access Token Wabot dan pastikan nombor WhatsApp masih bersambung.</div>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-100 shrink-0" onClick={ack} data-testid="ai-wa-ack"><CheckCheck className="h-3.5 w-3.5 mr-1" />Sudah semak</Button>
        </div>
      )}
      <AIWabotSettings />
      {KINDS.map(({ k, label, time }) => (
        <div key={k} className="flex items-center gap-2 py-1.5 border-t border-slate-50 first:border-0">
          <Switch checked={!!wa[k]} onCheckedChange={(v) => onSave({ wa_reports: { ...wa, [k]: v } })} data-testid={`ai-wa-switch-${k}`} />
          <div className="flex-1 text-[13px] text-slate-700"><b>{label}</b> <span className="text-slate-400 text-[11px]">• {time}</span></div>
          <Badge variant="outline" className={`text-[10px] ${wa[k] ? "border-emerald-300 text-emerald-700" : "text-slate-400"}`}>{wa[k] ? "ON" : "OFF"}</Badge>
        </div>
      ))}
      <div className="flex items-center gap-2 py-1.5 border-t border-slate-50">
        <Switch checked={settings.wa_monthly_pdf !== false} onCheckedChange={(v) => onSave({ wa_monthly_pdf: v })} data-testid="ai-wa-monthly-pdf" />
        <FileText className="h-4 w-4 text-violet-500" />
        <div className="flex-1 text-[12px] text-slate-700">Lampirkan <b>PDF bergambar</b> bersama laporan bulanan</div>
      </div>
      <div className="flex items-center gap-2 py-2 mt-1 rounded-xl bg-amber-50 px-2">
        <Switch checked={!!settings.wa_dry_run} onCheckedChange={(v) => onSave({ wa_dry_run: v })} data-testid="ai-wa-dryrun" />
        <FlaskConical className="h-4 w-4 text-amber-600" />
        <div className="flex-1 text-[12px] text-amber-800">Mod ujian (log sahaja, tidak dihantar ke WhatsApp)</div>
      </div>

      <div className="flex items-center mt-3 mb-1">
        <div className="font-bold text-[13px] text-slate-700 flex-1">Penerima ({recs.length})</div>
        {!adding && <Button size="sm" className="h-7 rounded-lg text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => setAdding(true)} data-testid="ai-wa-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />Tambah</Button>}
      </div>

      {adding && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-2 space-y-2 mb-2" data-testid="ai-wa-add-form">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Nama" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-8 text-xs bg-white" data-testid="ai-wa-name" />
            <Input placeholder="No. telefon (cth 0123456789)" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-8 text-xs bg-white" data-testid="ai-wa-phone" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, branch: v === "superadmin" ? "" : form.branch })}>
              <SelectTrigger className="h-8 text-xs bg-white" data-testid="ai-wa-type"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[10050]">
                <SelectItem value="pengurus">Pengurus Cawangan</SelectItem>
                <SelectItem value="superadmin">Superadmin (semua cawangan)</SelectItem>
              </SelectContent>
            </Select>
            {form.type === "pengurus" ? (
              <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                <SelectTrigger className="h-8 text-xs bg-white" data-testid="ai-wa-branch"><SelectValue placeholder="Pilih cawangan" /></SelectTrigger>
                <SelectContent className="z-[10050]">
                  <SelectItem value={ALL} data-testid="ai-wa-branch-all">Semua cawangan</SelectItem>
                  {branches.map((b) => <SelectItem key={b} value={b}>{b.replace("Destiny Junior ", "")}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : <div className="h-8 rounded-md border border-dashed border-slate-200 text-[11px] text-slate-400 grid place-items-center">Laporan semua cawangan</div>}
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setAdding(false); setForm(EMPTY); }}>Batal</Button>
            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={add} data-testid="ai-wa-save">Simpan</Button>
          </div>
        </div>
      )}

      {recs.length === 0 && !adding && <div className="text-xs text-slate-400 py-2">Belum ada penerima. Tekan "Tambah" untuk mula.</div>}
      <div className="space-y-2">
        {recs.map((r) => (
          <div key={r.id} className={`rounded-xl border p-2 transition-colors ${r.active ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-70"}`} data-testid={`ai-wa-rec-${r.id}`}>
            <div className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full grid place-items-center text-white ${r.type === "superadmin" ? "bg-fuchsia-500" : "bg-indigo-500"}`}>
                {r.type === "superadmin" ? <Crown className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold text-slate-800 truncate">{r.name}{r.source && <span className="ml-1.5 align-middle text-[9px] font-bold uppercase rounded bg-fuchsia-100 text-fuchsia-700 px-1.5 py-0.5" title={r.source === "env" ? "Nombor lalai dari tetapan server (SUPERADMIN_WHATSAPP)" : "Nombor dari Profil Superadmin"} data-testid="ai-wa-default-badge">Lalai {r.source === "env" ? "server" : "profil"}</span>}</div>
                <div className="text-[11px] text-slate-500 truncate">{r.phone} • {r.type === "superadmin" || r.branch === ALL ? "Semua cawangan" : r.branch.replace("Destiny Junior ", "")}</div>
              </div>
              <Switch checked={!!r.active} onCheckedChange={(v) => patch(r.id, { active: v })} data-testid={`ai-wa-active-${r.id}`} />
              <button onClick={() => remove(r)} className="h-7 w-7 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 grid place-items-center" aria-label="Padam" data-testid={`ai-wa-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="mt-2 grid grid-cols-4 gap-1">
              {KINDS.map(({ k, label }) => {
                const on = r.reports?.[k] !== false;
                return (
                  <div key={k} className={`rounded-lg border px-1.5 py-1 ${on ? "border-emerald-200 bg-emerald-50/60" : "border-slate-100 bg-slate-50"}`}>
                    <button onClick={() => patch(r.id, { reports: { ...r.reports, [k]: !on } })} data-testid={`ai-wa-kind-${r.id}-${k}`}
                      className={`w-full text-[10.5px] font-bold transition-colors ${on ? "text-emerald-700" : "text-slate-400 line-through"}`}>{label}</button>
                    <div className="flex justify-center gap-1 mt-0.5">
                      <button title="Pratonton" onClick={() => test(r, k, false)} className="h-6 w-6 rounded-md hover:bg-white grid place-items-center text-slate-500" data-testid={`ai-wa-preview-${r.id}-${k}`}>
                        {busy === `${r.id}:${k}:p` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                      <button title="Hantar ujian" onClick={() => test(r, k, true)} className="h-6 w-6 rounded-md hover:bg-white grid place-items-center text-emerald-600" data-testid={`ai-wa-send-${r.id}-${k}`}>
                        {busy === `${r.id}:${k}:s` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {settings.wa_monthly_pdf !== false && r.reports?.monthly !== false && (
              <div className="mt-1 flex items-center gap-1 rounded-lg bg-violet-50 border border-violet-100 px-2 py-1">
                <FileText className="h-3.5 w-3.5 text-violet-500" />
                <span className="flex-1 text-[11px] font-semibold text-violet-700">PDF Bulanan Bergambar</span>
                <button title="Jana & lihat PDF" onClick={() => test(r, "monthly_pdf", false)} className="h-6 px-1.5 rounded-md hover:bg-white text-[10px] font-bold text-violet-600 flex items-center gap-1" data-testid={`ai-wa-preview-${r.id}-monthly_pdf`}>
                  {busy === `${r.id}:monthly_pdf:p` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Lihat
                </button>
                <button title="Hantar ujian PDF" onClick={() => test(r, "monthly_pdf", true)} className="h-6 px-1.5 rounded-md hover:bg-white text-[10px] font-bold text-emerald-600 flex items-center gap-1" data-testid={`ai-wa-send-${r.id}-monthly_pdf`}>
                  {busy === `${r.id}:monthly_pdf:s` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Hantar
                </button>
              </div>
            )}
            <div className="mt-1 grid grid-cols-2 gap-1" data-testid={`ai-wa-extra-${r.id}`}>
              {[{ k: "urgent", label: "Amaran Segera", hint: "Nisbah & corak kecederaan" }, { k: "executive", label: "Laporan Eksekutif PDF", hint: "Ahad 8:00 pagi" },
                ...(r.type === "superadmin" ? [{ k: "briefing", label: "Taklimat Pagi", hint: "Harian 7:30 pagi" }] : [])].map(({ k, label, hint }) => {
                const on = r.reports?.[k] !== false;
                return (
                  <button key={k} onClick={() => patch(r.id, { reports: { ...r.reports, [k]: !on } })} data-testid={`ai-wa-kind-${r.id}-${k}`}
                    className={`rounded-lg border px-2 py-1 text-left transition-colors ${on ? "border-amber-200 bg-amber-50 text-amber-800" : "border-slate-100 bg-slate-50 text-slate-400 line-through"}`}>
                    <div className="text-[10.5px] font-bold">{label}</div>
                    <div className="text-[9.5px] opacity-80 no-underline">{hint}</div>
                  </button>
                );
              })}
            </div>
            {preview?.id === r.id && (
              <div className="mt-2 rounded-xl bg-[#e7fbe6] border border-emerald-200 p-2 relative" data-testid="ai-wa-preview-box">
                <button onClick={() => setPreview(null)} className="absolute top-1 right-1 h-6 w-6 rounded-md hover:bg-white/70 grid place-items-center text-slate-500"><X className="h-3.5 w-3.5" /></button>
                <div className="text-[10px] text-emerald-700 font-bold mb-1">Pratonton {preview.kind === "monthly_pdf" ? "PDF Bulanan" : KINDS.find((x) => x.k === preview.kind)?.label} • dari {preview.sender}{preview.status && preview.status !== "preview" ? ` • ${preview.status}` : ""}</div>
                <pre className="whitespace-pre-wrap text-[11px] leading-snug text-slate-800 max-h-64 overflow-y-auto font-sans">{preview.message}</pre>
                {preview.file_url && (
                  <a href={fileHref(preview.file_url)} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-violet-700 hover:underline" data-testid="ai-wa-pdf-link">
                    <ExternalLink className="h-3.5 w-3.5" /> Buka PDF
                  </a>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center mt-3 mb-1">
        <div className="font-bold text-[12px] text-slate-600 flex-1">Log Penghantaran WhatsApp</div>
        <button onClick={loadLogs} className="text-slate-400 hover:text-slate-600"><RefreshCw className="h-3.5 w-3.5" /></button>
      </div>
      {logs.length === 0 && <div className="text-[11px] text-slate-400">Belum ada penghantaran.</div>}
      {logs.slice(0, 10).map((l) => (
        <div key={l.id} className="text-[11px] py-1 border-t border-slate-50 flex items-center gap-2" data-testid="ai-wa-log-row">
          <Badge variant="outline" className={`text-[10px] ${l.status === "sent" || l.status === "received" ? "border-emerald-300 text-emerald-700" : l.status === "dry_run" ? "border-amber-300 text-amber-700" : l.status === "ignored" ? "border-slate-300 text-slate-500" : "border-rose-300 text-rose-700"}`}>{l.status}</Badge>
          <div className="flex-1 min-w-0 truncate text-slate-600" title={l.response}>{l.recipient_name} • {logLabel(l.type)}</div>
          <div className="text-slate-400">{new Date(l.created_at).toLocaleString("ms-MY", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
        </div>
      ))}
    </div>
  );
}
